import { LitElement, html, css } from 'lit';

export class SettingsTab extends LitElement {
    static properties = {
        contextManager: { type: Object },
        activeContextId: { type: String },
        contextsData: { state: true },
        editedContextsDataText: { state: true }
    };

    static styles = css`
        :host {
            display: block;
            padding: 24px;
        }

        .settings-container {
            max-width: 800px;
        }

        .settings-section {
            margin-bottom: 32px;
            padding: 24px;
            background-color: var(--spectrum-gray-50);
            border-radius: 8px;
            border: 1px solid var(--spectrum-gray-300);
        }

        .settings-section h3 {
            margin: 0 0 16px 0;
            font-size: 18px;
            font-weight: 600;
            color: var(--spectrum-gray-800);
        }

        .settings-section p {
            margin: 0 0 16px 0;
            color: var(--spectrum-gray-700);
            line-height: 1.5;
        }
        
        sp-textfield[multiline] {
            width: 100%;
            height: 300px;
            font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
            font-size: 14px; /* Slightly bigger font */
        }
        
        .button-group {
            margin-top: 16px;
            display: flex;
            gap: 8px;
        }
    `;

    constructor() {
        super();
        this.contextManager = null;
        this.activeContextId = null;
        this.contextsData = null;
        this.editedContextsDataText = '';
    }

    connectedCallback() {
        super.connectedCallback();
        this.loadContextsData();
    }

    loadContextsData() {
        try {
            const rawData = localStorage.getItem('contexts');
            if (rawData) {
                this.contextsData = JSON.parse(rawData);
                this.editedContextsDataText = JSON.stringify(this.contextsData, null, 2);
            } else {
                this.contextsData = null;
                this.editedContextsDataText = 'No contexts data found in localStorage.';
            }
            console.log(this.contextsData);
        } catch (error) {
            console.error('Error parsing contexts from localStorage:', error);
            this.contextsData = { error: 'Failed to parse contexts data' };
            this.editedContextsDataText = JSON.stringify(this.contextsData, null, 2);
        }
    }

    refreshContextsData() {
        this.loadContextsData();
    }
    
    handleTextareaInput(e) {
        this.editedContextsDataText = e.target.value;
    }
    
    saveContextsData() {
        try {
            // Validate JSON before saving
            JSON.parse(this.editedContextsDataText);
            localStorage.setItem('contexts', this.editedContextsDataText);
            this.loadContextsData();
        } catch (error) {
            console.error('Error saving contexts to localStorage: Invalid JSON', error);
            // Optionally, you could show an error to the user here
        }
    }

    render() {
        return html`
            <div class="settings-container">
                <h2>Settings</h2>
                
                <div class="settings-section">
                    <h3>LocalStorage Contexts Data</h3>
                    <p>View and edit the 'contexts' key from localStorage. Make sure it is valid JSON before saving.</p>

                    <sp-accordion>
                        <sp-accordion-item label="View/Edit Contexts">
                            <sp-textfield
                                multiline
                                .value="${this.editedContextsDataText}"
                                @input="${this.handleTextareaInput}">
                            </sp-textarea>
                        </sp-accordion-item>
                    </sp-accordion>
                    
                    <div class="button-group">
                        <sp-button 
                            variant="primary" 
                            @click="${this.saveContextsData}">
                            Save to LocalStorage
                        </sp-button>
                        <sp-button 
                            variant="secondary" 
                            @click="${this.refreshContextsData}">
                            Refresh from LocalStorage
                        </sp-button>
                    </div>
                </div>
            </div>
        `;
    }
}

// Only define the custom element if it hasn't been defined already
if (!customElements.get('settings-tab')) {
    customElements.define('settings-tab', SettingsTab);
} 